import React, { useState, useRef, useEffect } from "react";

const injectUploadZoneStyles = () => {
    if (document.getElementById("qual-upload-zone-styles")) return;
    const s = document.createElement("style");
    s.id = "qual-upload-zone-styles";
    s.textContent = `
        :root {
            --ff:'DM Sans','Segoe UI',sans-serif;
            --bdr:#e4e8f0; --bdr2:#f0f2f7; --card:#fff;
            --ink:#0f172a; --ink2:#334155; --ink3:#64748b; --ink4:#94a3b8;
            --blue:#3b82f6; --blue2:#bfdbfe; --blue3:#eff6ff;
            --red:#dc2626; --red2:#fee2e2;
        }
        .qduz-drop {
            border:2px dashed var(--bdr);border-radius:10px;padding:22px 18px;
            text-align:center;background:var(--bdr2);cursor:pointer;
            transition:border-color .15s,background .15s;
        }
        .qduz-drop:hover, .qduz-drop.drag { border-color:var(--blue);background:var(--blue3); }
        .qduz-drop.disabled { opacity:0.5;cursor:not-allowed;pointer-events:none; }
        .qduz-selected {
            border:1px solid var(--blue2);
            border-radius:9px;
            overflow:hidden;
            width:100%;
            max-width:100%;
            box-sizing:border-box;
        }
        .qduz-selected-hd {
            display:flex;
            align-items:center;
            justify-content:space-between;
            padding:9px 13px;
            background:var(--blue3);
            gap:12px;
            min-width:0;
        }
        .qduz-selected-hd > div {
            min-width:0;
            flex:1;
        }
        .qduz-selected-hd .file-name {
            overflow:hidden;
            text-overflow:ellipsis;
            white-space:nowrap;
        }
        .qduz-replace {
            background:none;border:none;cursor:pointer;
            font-size:11px;color:var(--ink4);font-family:var(--ff);
            padding:2px 8px;border-radius:5px;flex-shrink:0;
        }
        .qduz-replace:hover { color:var(--ink2); }
        .qduz-attach-btn {
            display:flex;align-items:center;justify-content:center;gap:6px;
            width:100%;padding:0 14px;height:32px;border-radius:7px;
            font-size:12px;font-weight:600;cursor:pointer;border:none;
            font-family:var(--ff);background:var(--blue);color:#fff;
            transition:background .15s;
        }
        .qduz-attach-btn:hover:not(:disabled) { background:#2563eb; }
        .qduz-attach-btn:disabled { opacity:0.55;cursor:not-allowed; }
        @keyframes qduzSpin { to { transform:rotate(360deg); } }
        .qduz-spin {
            animation:qduzSpin .7s linear infinite;display:inline-block;
            border-radius:50%;flex-shrink:0;width:12px;height:12px;
            border:2px solid rgba(255,255,255,0.35);border-top-color:#fff;
        }
        .qduz-saved {
            border:1px solid var(--blue2);border-radius:9px;
            padding:10px 13px;background:var(--blue3);
            display:flex;align-items:center;gap:10px;min-width:0;
        }
        .qduz-err {
            display:flex;align-items:center;gap:6px;margin-top:7px;
            padding:7px 11px;border-radius:7px;background:var(--red2);
            border:1px solid #fca5a5;font-size:11.5px;color:var(--red);font-weight:600;
        }
        /* multi-file chips */
        .qduz-chips {
            display:flex;flex-wrap:wrap;gap:6px;padding:10px 13px 0;
        }
        .qduz-chip {
            display:inline-flex;align-items:center;gap:5px;
            padding:3px 8px;border-radius:20px;
            background:#e0eaff;border:1px solid var(--blue2);
            font-size:11px;font-weight:600;color:var(--ink2);
            font-family:var(--ff);max-width:180px;
        }
        .qduz-chip span {
            overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
        }
        .qduz-chip-remove {
            background:none;border:none;cursor:pointer;
            color:var(--ink3);font-size:13px;line-height:1;padding:0;
            flex-shrink:0;
        }
        .qduz-chip-remove:hover { color:var(--red); }
    `;
    document.head.appendChild(s);
};

const UploadIcon = ({ color = "var(--blue)" }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ flexShrink: 0, width: 14, height: 14 }}>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
);

/**
 * Props:
 *   onFile      {function(File|File[])}  — single File in single mode; File[] in multi mode
 *   pendingFile {File|null}             — single-file mode: currently selected pending file
 *   pendingFiles {File[]}               — multi-file mode: currently selected pending files
 *   onClear     {function}              — called to clear selection (single or all)
 *   onRemoveFile {function(index)}      — called to remove one file from multi list
 *   disabled    {boolean}
 *   onAttach    {function}              — optional inline attach handler (shown when provided)
 *   uploading   {boolean}              — show spinner + disable during upload
 *   uploadErr   {string}               — error message to show below attach button
 *   savedDoc    {{ fileName, url }|null} — already-saved document to display (single mode only)
 *   multi       {boolean}              — enable multiple file selection (default false)
 */
const QualDocUploadZone = ({
    onFile,
    pendingFile,
    pendingFiles = [],
    onClear,
    onRemoveFile,
    disabled,
    onAttach,
    uploading,
    uploadErr,
    savedDoc,
    multi = false,
}) => {
    useEffect(() => { injectUploadZoneStyles(); }, []);
    useEffect(() => { if (savedDoc) setReplacing(false); }, [savedDoc]);

    const [drag, setDrag] = useState(false);
    const [replacing, setReplacing] = useState(false);
    const fileRef = useRef(null);

    const handleDrop = (e) => {
        if (disabled || uploading) return;
        e.preventDefault();
        setDrag(false);
        if (multi) {
            const files = Array.from(e.dataTransfer.files || []);
            if (files.length) onFile(files);
        } else {
            const f = e.dataTransfer.files?.[0];
            if (f) onFile(f);
        }
    };

    const handleInputChange = (e) => {
        if (multi) {
            const files = Array.from(e.target.files || []);
            if (files.length) onFile(files);
        } else {
            if (e.target.files[0]) onFile(e.target.files[0]);
        }
        // reset input so the same file can be re-selected
        e.target.value = "";
    };

    // ── Saved doc state (single mode only) ──
    if (!multi && savedDoc && !pendingFile && !replacing) {
        return (
            <div className="qduz-saved">
                <UploadIcon />
                <div style={{ flex: 1, minWidth: 0 }}>
                    {savedDoc.url ? (
                        <a href={savedDoc.url} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: 12, fontWeight: 700, color: "var(--blue)", textDecoration: "underline", wordBreak: "break-all" }}>
                            {savedDoc.fileName}
                        </a>
                    ) : (
                        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink)" }}>{savedDoc.fileName}</span>
                    )}
                    <div style={{ fontSize: 10.5, color: "var(--ink3)", marginTop: 1 }}>attached</div>
                </div>
                {!disabled && !uploading && (
                    <button className="qduz-replace" onClick={() => { setReplacing(true); onClear?.(); }}>
                        ← replace
                    </button>
                )}
            </div>
        );
    }

    // ── Multi-file mode: show chips for selected files ──
    if (multi && pendingFiles.length > 0) {
        return (
            <div className="qduz-selected">
                <div className="qduz-chips">
                    {pendingFiles.map((f, i) => (
                        <div key={i} className="qduz-chip">
                            <span title={f.name}>{f.name}</span>
                            {!uploading && (
                                <button
                                    className="qduz-chip-remove"
                                    onClick={() => onRemoveFile?.(i)}
                                    title="Remove"
                                >×</button>
                            )}
                        </div>
                    ))}
                </div>
                {/* Add more files button */}
                <div style={{ padding: "8px 13px 0", display: "flex", gap: 8, alignItems: "center" }}>
                    <button
                        className="qduz-replace"
                        style={{ padding: "4px 10px", border: "1px dashed var(--bdr)", borderRadius: 6 }}
                        disabled={uploading || disabled}
                        onClick={() => fileRef.current?.click()}
                    >
                        + add more
                    </button>
                    <span style={{ fontSize: 10, color: "var(--ink4)", fontFamily: "var(--ff)" }}>
                        {pendingFiles.length} file{pendingFiles.length !== 1 ? "s" : ""} selected
                    </span>
                </div>
                {onAttach && (
                    <div style={{ padding: "8px 13px 10px" }}>
                        <button
                            className="qduz-attach-btn"
                            disabled={uploading || disabled}
                            onClick={onAttach}
                        >
                            {uploading && <span className="qduz-spin" />}
                            {uploading ? "Uploading…" : `Upload ${pendingFiles.length} File${pendingFiles.length !== 1 ? "s" : ""}`}
                        </button>
                        {uploadErr && <div className="qduz-err">⚠ {uploadErr}</div>}
                    </div>
                )}
                <input
                    ref={fileRef}
                    type="file"
                    hidden
                    multiple
                    disabled={disabled || uploading}
                    accept=".csv,.xlsx,.xls,.pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={handleInputChange}
                />
            </div>
        );
    }

    // ── Single-file mode: file selected (pending upload) ──
    if (!multi && pendingFile) {
        return (
            <div className="qduz-selected">
                <div className="qduz-selected-hd">
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <UploadIcon />
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 12, color: "var(--ink)" }}>
                                {pendingFile.name}
                            </div>
                            <div style={{ fontSize: 10.5, color: "var(--ink3)", marginTop: 1 }}>
                                {Math.round(pendingFile.size / 1024)} KB · ready to attach
                            </div>
                        </div>
                    </div>
                    {!uploading && (
                        <button className="qduz-replace" onClick={onClear}>
                            ← replace
                        </button>
                    )}
                </div>
                {onAttach && (
                    <div style={{ padding: "0 13px 10px" }}>
                        <button
                            className="qduz-attach-btn"
                            disabled={uploading || disabled}
                            onClick={onAttach}
                        >
                            {uploading && <span className="qduz-spin" />}
                            {uploading ? "Uploading…" : "Attach Document"}
                        </button>
                        {uploadErr && <div className="qduz-err">⚠ {uploadErr}</div>}
                    </div>
                )}
            </div>
        );
    }

    // ── Drop zone ──
    return (
        <div
            className={`qduz-drop${drag ? " drag" : ""}${(disabled || uploading) ? " disabled" : ""}`}
            onDragOver={(e) => { if (!disabled && !uploading) { e.preventDefault(); setDrag(true); } }}
            onDragLeave={() => setDrag(false)}
            onDrop={handleDrop}
            onClick={() => !disabled && !uploading && fileRef.current?.click()}
        >
            <input
                ref={fileRef}
                type="file"
                hidden
                multiple={multi}
                disabled={disabled || uploading}
                accept=".csv,.xlsx,.xls,.pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={handleInputChange}
            />
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--ink4)" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"
                style={{ margin: "0 auto 8px", display: "block", width: 20, height: 20, flexShrink: 0 }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink2)", marginBottom: 8 }}>
                {multi ? "Drop files or click to browse" : "Drop a file or click to browse"}
            </div>
            <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
                {[
                    { label: "PDF / DOC", sub: "stored as proof" },
                    { label: "JPG / PNG", sub: "stored as proof" },
                ].map(({ label, sub }) => (
                    <div key={label} style={{ padding: "4px 10px", borderRadius: 6, background: "var(--card)", border: "1px solid var(--bdr)" }}>
                        <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--ink2)" }}>{label}</div>
                        <div style={{ fontSize: 9.5, color: "var(--ink4)" }}>{sub}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default QualDocUploadZone;
